<template>
  <Teleport defer to="ion-app">
    <!--
      A sheet, not a centred dialog: this opens from a control paired with the
      wallet's filter, which is a sheet — two identical-looking buttons must not
      present two different ways. A sheet also arrives from the edge it can be
      thrown back to, and stays grabbable mid-animation; a centred dialog can
      only be waited out.

      The backdrop stays on (no `backdrop-breakpoint` matching the presented
      breakpoint): this is a modal task, so dimming the page behind and keeping
      the focus trap is correct. That prop is for non-blocking panels.
    -->
    <ion-modal
      ref="ionModal"
      v-bind="privateRegionAttrs"
      class="hcs-modal"
      v-theme-scope="themeScope"
      :can-dismiss="!submitting"
      :backdrop-dismiss="!submitting"
      :show-backdrop="true"
      :initial-breakpoint="1"
      :breakpoints="[0, 1]"
      :keep-contents-mounted="true"
      :style="keyboardStyle"
      aria-labelledby="have-a-code-title"
    >
      <div class="hcs-card">
        <div class="hcs-text">
          <h2 id="have-a-code-title" class="hcs-title">Have a code?</h2>
          <p class="hcs-subtitle">Redeem your voucher</p>
        </div>

        <form class="hcs-form" novalidate @submit.prevent="submit">
          <!--
            Nothing is prefilled and nothing is judged. The member types what
            is on the card — the whole code, or just the sixteen characters
            after the prefix — and it is sent exactly as typed, grouped in
            fours. The server supplies a missing prefix; IDR says whether the
            card is good.

            data-compact-input: main.scss forces every bare text input to a
            55px white box with an 8px radius (!important), which would paint a
            white block on the dark sheet; this input opts out so the rules
            below are the ones that apply.
          -->
          <input
            ref="codeField"
            v-model="displayCode"
            class="hcs-input"
            :class="{ 'hcs-input--error': !!errorMessage }"
            type="text"
            data-compact-input
            inputmode="text"
            autocapitalize="characters"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            placeholder="Enter your code"
            :aria-invalid="!!errorMessage"
            aria-describedby="have-a-code-error"
            :disabled="submitting"
            @input="onInput"
          />

          <!-- Reserved line: keeps the card from jumping when an error appears. -->
          <p id="have-a-code-error" class="hcs-error" role="alert">
            {{ errorMessage }}
          </p>

          <button type="submit" class="hcs-btn hcs-btn--primary" :disabled="!canSubmit">
            {{ submitting ? 'Checking…' : 'Unlock' }}
          </button>
          <button
            type="button"
            class="hcs-btn hcs-btn--secondary"
            :disabled="submitting"
            @click="close"
          >
            Cancel
          </button>
        </form>
      </div>
    </ion-modal>
  </Teleport>
</template>

<script>
import { IonModal } from '@ionic/vue';
import coursesTheme from '@/mixins/CoursesTheme';
import { codeForSubmit, formatIdrCodeInput, isCollectionPass } from '@/utils/idr-code';
import { REDEEM_MESSAGES, redeemErrorMessage } from '@/utils/redeem-messages';
import privateRegion from '@/mixins/PrivateRegion';
import keyboardOverlap from '@/mixins/keyboard-overlap';

// The no-camera path onto the same lane as a QR scan: same store action, same
// server call, same copy. Ships in v1 rather than as a fallback — a member whose
// camera permission is off, or whose card has a scuffed QR, still has a way in.
export default {
  name: 'HaveACodeSheet',
  components: { IonModal },
  // privateRegion: teleported out of the page root, so the page's stamp does
  // not reach it — the attributes are bound on the modal host above.
  mixins: [coursesTheme, privateRegion, keyboardOverlap],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'redeemed', 'needs-cg'],
  data() {
    return {
      // What the member sees: progressively dashed as they type, with or
      // without the prefix. The canonical code is derived from it — see `code`.
      displayCode: '',
      errorMessage: '',
      submitting: false,
      // Keyboard state comes from the keyboard-overlap mixin, subscribed while the
      // sheet is up: where the keyboard overlays the webview it hides the field the
      // member is typing into, so the sheet pads itself by the strip that is actually
      // covered — the same way the notes editor and the highlight drawer do. Stays 0
      // on web/desktop, where the plugin is unavailable and nothing overlays anything.
    };
  },
  computed: {
    // What the server gets: the code exactly as typed, tidied — never judged
    // and never completed here. A member who leaves the prefix off gets it
    // supplied server-side, where both IDRU and IDRV can be tried against IDR.
    // '' only while there is nothing to send.
    code() {
      return codeForSubmit(this.displayCode);
    },
    // Exposed as a custom property on the modal host so the sheet's own content
    // box (::part(content)) can reserve the keyboard's footprint below itself.
    // Custom properties inherit into the shadow tree, which the sheet's inner
    // parts are; a plain padding on .hcs-card would be overwritten by the
    // breakpoint rules that restate the padding shorthand.
    // keyboardOffsetPx is the mixin's: the strip the keyboard actually covers, which
    // is 0 where the window resized for it and the sheet has already been carried
    // clear.
    keyboardStyle() {
      return { '--keyboard-offset': `${this.keyboardOffsetPx}px` };
    },
    // Local shape check only — it stops an obviously-incomplete code from
    // costing a round trip. Whether a well-formed card is real, unspent and
    // unexpired is IDR's call alone, never ours.
    canSubmit() {
      return !this.submitting && this.code !== '';
    },
  },
  watch: {
    // Driven programmatically: `:is-open` does not reliably present when it
    // flips after mount — see docs/ION_MODAL_PATTERN.md.
    async modelValue(open) {
      if (open) {
        this.reset();
        // Before presenting, not after: `present()` resolves when the enter
        // animation has finished, and a member who taps the field while the
        // sheet is still sliding up raises the keyboard before that — every
        // keyboard event fired in those ~300ms would land on nothing, and the
        // sheet would stay flush against a keyboard covering the field for the
        // rest of the session. Not awaited, for the same reason: the listeners
        // must be arming while the animation runs, not gating it.
        this.startKeyboardOverlapTracking();
        await this.presentModal();
        this.$nextTick(() => this.$refs.codeField?.focus());
        return;
      }
      await this.dismissModal();
    },
  },
  async mounted() {
    if (this.modelValue) {
      this.startKeyboardOverlapTracking();
      await this.presentModal();
    }
  },
  beforeUnmount() {
    this.stopKeyboardOverlapTracking();
  },
  methods: {
    // The overlay is driven programmatically because `:is-open` does not
    // reliably present after mount (docs/ION_MODAL_PATTERN.md). Guarded because
    // `$el` is a custom element: before it upgrades, present/dismiss are simply
    // not there yet, and an unguarded call throws inside a lifecycle hook.
    async presentModal() {
      const el = this.$refs.ionModal?.$el;
      if (typeof el?.present !== 'function') return;
      await el.present();
      // Ionic's overlay contract, NOT a DOM event. `@ionModalDidDismiss` in a
      // template subscribes to `ion-modal-did-dismiss` (Vue hyphenates DOM event
      // names) while Ionic dispatches `ionModalDidDismiss` — so the binding this
      // replaced never fired. Worse, on a device a swipe-to-dismiss fires none of
      // the three spellings, and a sheet can be swiped. Without this the parent
      // would keep `modelValue: true` after a swipe and the trigger would look
      // dead. `onDidDismiss()` resolves however the sheet closed — swipe,
      // backdrop, hardware back, or Cancel.
      const dismissed = el.onDidDismiss?.();
      dismissed?.then?.(() => this.onDidDismiss());
    },
    async dismissModal() {
      this.stopKeyboardOverlapTracking();
      const el = this.$refs.ionModal?.$el;
      if (typeof el?.dismiss === 'function') await el.dismiss();
    },
    // Covers the routes out that Ionic owns — backdrop tap, swipe, Android back
    // — so the parent's state follows the modal rather than drifting from it.
    // Idempotent: `close()` emits directly too, and a dismissal the parent has
    // already been told about must not be announced twice.
    onDidDismiss() {
      this.stopKeyboardOverlapTracking();
      if (this.modelValue) this.$emit('update:modelValue', false);
    },
    reset() {
      this.displayCode = '';
      this.errorMessage = '';
      this.submitting = false;
      this.resetKeyboardOverlap();
    },
    onInput() {
      this.displayCode = formatIdrCodeInput(this.displayCode);
      // A collection pass (IDRC-) is a real card from the other IDR flow, and
      // it can never complete here — so say so as they type, rather than leaving
      // Unlock silently disabled. Anything else clears a stale failure the
      // moment they start correcting it.
      this.errorMessage = isCollectionPass(this.displayCode) ? REDEEM_MESSAGES.COLLECTION_PASS : '';
    },
    close() {
      if (this.submitting) return;
      this.$emit('update:modelValue', false);
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.errorMessage = '';

      // Captured before the await: the field stays disabled while in flight,
      // but the emit below must name the code that was actually sent.
      const code = this.code;
      let result;
      try {
        result = await this.$store.dispatch('courses/redeemBookCode', { code });
      } catch (error) {
        this.errorMessage = redeemErrorMessage(error);
        this.submitting = false;
        return;
      }

      // The server can refuse to grant until this member has a main CG. Not an
      // error — hand it to the page, which puts the CG block up and finishes the
      // redemption once they answer.
      if (result?.needsCgLink) {
        this.submitting = false;
        this.$emit('update:modelValue', false);
        this.$emit('needs-cg', code);
        return;
      }

      // Mock mode (and any 200-with-error API) reports failure in the body.
      if (result?.error) {
        this.errorMessage = result.error;
        this.submitting = false;
        return;
      }

      const programSlug = result?.unlockedProgramSlug;
      if (!programSlug) {
        this.errorMessage = REDEEM_MESSAGES.DEFAULT;
        this.submitting = false;
        return;
      }

      this.submitting = false;
      this.$emit('update:modelValue', false);
      // The parent owns navigation — it knows whether it is already on the
      // program landing or needs to push to it.
      this.$emit('redeemed', programSlug);
    },
  },
};
</script>

<style scoped>
.hcs-modal {
  --width: 100%;
  --max-width: 100%;
  --height: auto;
  /* Top corners only — the sheet is attached to the bottom edge. */
  --border-radius: 22px 22px 0 0;
  --background: var(--courses-surface-raised);
}

/* Ionic paints `.modal-wrapper`, so the sheet's own surface has to be set on the
   part rather than only on the card inside it. */
.hcs-modal::part(content) {
  background: var(--courses-surface-raised);
  border-radius: 22px 22px 0 0;
  /* The sheet is bottom-anchored with `--height: auto`, so padding below the card
     grows it upward: the field and Unlock rise clear of the keyboard instead of
     sitting under it. Painted in the sheet's own surface colour, so the reserved
     strip reads as the sheet continuing behind the keyboard. 0 where the window
     resized for the keyboard: the sheet has already been carried clear, and padding
     again would lift it a second keyboard's worth. */
  padding-bottom: var(--keyboard-offset, 0px);
  transition: padding-bottom 200ms ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .hcs-modal::part(content) {
    transition: none;
  }
}

/* Ionic's drag handle — the affordance that says this can be thrown back down.
   Same dimensions as the house ModalSheet so the two sheets read as one idiom. */
.hcs-modal::part(handle) {
  width: 40px;
  height: 5px;
  margin: 8px auto;
  border-radius: 3px;
  background-color: var(--courses-track);
}

.hcs-card {
  display: flex;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  flex-direction: column;
  gap: 16px;
  /* Ionic's drag handle is absolutely positioned at the top of the wrapper, not
     a flow element — so the top padding has to clear it (5px bar + 8px margins)
     or it lands on the title. Bottom padding clears the home indicator. */
  padding: 30px 24px max(24px, env(safe-area-inset-bottom));
  text-align: center;
  background-color: var(--courses-surface-raised);
  border-radius: 22px 22px 0 0;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
}

.hcs-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hcs-title {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.25;
  color: var(--courses-ink-strong);
}

.hcs-subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.65;
  color: var(--courses-ink-muted);
}

.hcs-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hcs-input {
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1.5px solid var(--courses-ink-muted);
  outline: none;
  background-color: transparent;
  color: var(--courses-ink-strong);
  font-size: 16px;
  font-weight: 700;
  /* Codes are read off a card character by character — a monospace face keeps
     the groups the same width so a misread is easier to spot. */
  font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
  letter-spacing: 0.06em;
  text-align: center;
  text-transform: uppercase;
}

.hcs-input:focus {
  border-color: var(--courses-ink-strong);
}

.hcs-input::placeholder {
  color: var(--courses-ink-muted);
  opacity: 0.6;
  font-weight: 400;
  /* The placeholder is a sentence, not a code: set it in the sheet's own text
     face rather than the code face. (`inherit` would take the input's monospace.) */
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  letter-spacing: normal;
  text-transform: none;
}

.hcs-input--error {
  border-color: #c4443a;
}

/* Below the xs breakpoint a whole typed code — IDRU, four groups, four dashes,
   24 monospace characters — no longer fits the field at 16px with tracking:
   the tail scrolls out of view. Pull the metrics in rather than let a member
   wonder where the last group went. Measured: 20px over at 320px otherwise. */
@media (max-width: 374px) {
  .hcs-input {
    padding: 14px 12px;
    font-size: 15px;
    letter-spacing: 0.02em;
  }
}

.hcs-input:disabled {
  opacity: 0.6;
}

.hcs-error {
  /* Always in the layout so the card doesn't jump when a message appears. */
  min-height: 18px;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #c4443a;
  text-align: left;
}

.hcs-btn {
  width: 100%;
  border-radius: 100px;
  cursor: pointer;
  transition: transform 100ms ease-out;
}

/* Every other tappable surface in this flow highlights on press — the wallet's
   chips, the voucher cards, the filter rows. These two were the exception. */
.hcs-btn:active:not(:disabled) {
  transform: scale(0.97);
}

@media (prefers-reduced-motion: reduce) {
  .hcs-btn {
    transition: none;
  }
}

.hcs-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.hcs-btn--primary {
  padding: 14px 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--courses-surface-raised);
  background-color: var(--courses-ink-strong);
  border: none;
}

.hcs-btn--secondary {
  padding: 12px 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--courses-ink-muted);
  background-color: transparent;
  border: none;
}

/* sm breakpoint (640px) */
@media (min-width: 640px) {
  .hcs-modal {
    --width: 380px;
  }
  .hcs-card {
    gap: 20px;
    padding: 36px;
  }
  .hcs-title {
    font-size: 24px;
  }
  .hcs-subtitle {
    font-size: 16px;
  }
  .hcs-btn--primary {
    padding: 16px 0;
    font-size: 16px;
  }
}

/* md breakpoint (768px) */
@media (min-width: 768px) {
  .hcs-modal {
    --width: 440px;
  }
  .hcs-card {
    padding: 40px;
  }
  .hcs-title {
    font-size: 30px;
  }
  .hcs-subtitle {
    font-size: 18px;
  }
  .hcs-btn--primary {
    padding: 20px 0;
    font-size: 18px;
  }
}
</style>
